import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import { Box, Chip, Paper, Tooltip, Typography } from '@material-ui/core'
import {
  AlertTriangle as AlertTriangleIcon,
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  X as XIcon,
} from 'react-feather'

import FilenameChip from '../../../FilenameChip'
import { FindingCard, OverlayCard } from './components'

import constants from 'constants/index'
import useStyles from './styles'

const ClaimCard = ({
  claim,
  decision,
  findings,
  overlays,
  isOpen,
  onToggle,
}) => {
  const { CLAIM_TYPE_LABEL, DS010_ITEM_LABEL, STRENGTH_LABEL } =
    constants.dai.disputes

  const classes = useStyles()

  const admissible = decision.is_admissible
  const strengthLabel = STRENGTH_LABEL[decision.strength] || decision.strength
  const typeLabel = CLAIM_TYPE_LABEL[claim.primary_type] || claim.primary_type
  const ds010ItemLabel =
    claim.ds010_item &&
    (DS010_ITEM_LABEL[claim.ds010_item] || `item ${claim.ds010_item}`)
  const fallback = decision.recommended_fallback_letter
  const hasFallback =
    decision.target_alternative_feasible === false && !!fallback

  return (
    <Paper
      variant="outlined"
      className={clsx(classes.claimPaper, {
        [classes.claimPaperAdmissible]: admissible,
        [classes.claimPaperGroundless]: !admissible,
      })}
    >
      <Box className={classes.claimHeader} onClick={onToggle}>
        <Box className={classes.headerMain}>
          <Box className={classes.headerTopRow}>
            <Typography className={classes.claimType}>
              {typeLabel}
              {ds010ItemLabel && ` · ${ds010ItemLabel}`}
              {claim.target_requirement_id &&
                ` · req. ${claim.target_requirement_id}`}
            </Typography>
            {hasFallback && (
              <Tooltip
                title={`A tese sustenta apenas a alternativa ${fallback}, não a pleiteada.`}
              >
                <Chip
                  size="small"
                  icon={<AlertTriangleIcon size={11} />}
                  label={`Sustenta até ${fallback}`}
                  className={classes.fallbackBadge}
                />
              </Tooltip>
            )}
          </Box>
          {claim.excerpt_literal && (
            <Typography className={classes.excerptCollapsed}>
              "{claim.excerpt_literal}"
            </Typography>
          )}
        </Box>

        <Box className={classes.statusGroup}>
          {strengthLabel && (
            <Chip
              size="small"
              label={strengthLabel}
              className={classes.strengthChip}
            />
          )}
          <Tooltip
            title={
              admissible
                ? 'A DAI identificou fundamento para contestar este ponto do auditor.'
                : 'A DAI não identificou fundamento suficiente para contestar este ponto.'
            }
          >
            <Chip
              size="small"
              icon={admissible ? <CheckIcon size={12} /> : <XIcon size={12} />}
              label={admissible ? 'Com fundamento' : 'Sem fundamento'}
              className={
                admissible
                  ? classes.statusChipAdmissible
                  : classes.statusChipGroundless
              }
            />
          </Tooltip>
          <ChevronDownIcon
            size={18}
            className={clsx(classes.expandIcon, {
              [classes.expandIconOpen]: isOpen,
            })}
          />
        </Box>
      </Box>

      {isOpen && (
        <Box className={classes.expandedBody}>
          {claim.excerpt_literal && (
            <>
              <Typography className={classes.fieldLabel}>
                Trecho do auditor
              </Typography>
              <Typography className={classes.excerpt}>
                {claim.excerpt_literal}
              </Typography>
            </>
          )}

          {admissible && decision.approved_thesis && (
            <>
              <Typography className={classes.fieldLabel}>
                Tese aprovada
              </Typography>
              <Typography className={classes.thesis}>
                {decision.approved_thesis}
              </Typography>
            </>
          )}

          {!admissible && decision.exclusion_reason && (
            <>
              <Typography className={classes.fieldLabel}>
                Motivo de exclusão
              </Typography>
              <Typography className={classes.thesis}>
                {decision.exclusion_reason}
              </Typography>
            </>
          )}

          {!isEmpty(decision.authorized_evidences) && (
            <>
              <Typography className={classes.fieldLabel}>
                Evidências autorizadas
              </Typography>
              <Box className={classes.evidencesRow}>
                {decision.authorized_evidences.map((file) => (
                  <FilenameChip key={file} filename={file} />
                ))}
              </Box>
            </>
          )}

          {!isEmpty(findings) && (
            <>
              <Typography className={classes.fieldLabel}>
                Achados dos especialistas
              </Typography>
              {findings.map((finding, index) => (
                <FindingCard key={index} finding={finding} />
              ))}
            </>
          )}

          {!isEmpty(overlays) && (
            <>
              <Typography className={classes.fieldLabel}>
                Achados derivados
              </Typography>
              {overlays.map((overlay, index) => (
                <OverlayCard key={index} overlay={overlay} />
              ))}
            </>
          )}
        </Box>
      )}
    </Paper>
  )
}

ClaimCard.propTypes = {
  claim: PropTypes.object.isRequired,
  decision: PropTypes.object.isRequired,
  findings: PropTypes.array,
  overlays: PropTypes.array,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
}

ClaimCard.defaultProps = {
  findings: [],
  overlays: [],
  isOpen: false,
}

export default ClaimCard
